import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import type { AuditEntry } from '../../api/types'
import { Button, Card, EmptyState, ErrorNote, Field, LoadingBlock } from '../../components/ui'
import { useAudit } from '../../hooks/useAdmin'
import { timeAgo } from '../../utils/format'

/** The kinds of action worth filtering to, most useful first. */
const ACTIONS: [string, string][] = [
  ['', 'Everything'],
  ['workspace.', 'Workspaces: plans, stopping, deleting'],
  ['member.', 'Members and invitations'],
  ['credits.', 'Credit grants'],
  ['live.', 'Live: arming, stream keys'],
  ['api_key.', 'API keys'],
  ['settings.', 'Provider settings'],
  ['jobs.', 'Jobs cancelled or retried'],
]

/** A short line for what an entry's detail says, without dumping raw JSON. */
function describe(entry: AuditEntry): string {
  const parts = Object.entries(entry.detail)
    .filter(([, value]) => value !== null && value !== '' && typeof value !== 'object')
    .map(([key, value]) => `${key.replace(/_/g, ' ')}: ${String(value)}`)
  return parts.join(' · ')
}

/** Who did what, newest first. Filter by workspace (`?workspace=`) and by kind of action. */
export function AdminAuditPage() {
  const [params, setParams] = useSearchParams()
  const workspaceId = params.get('workspace') ?? undefined
  const [action, setAction] = useState('')
  const audit = useAudit({ workspaceId, action: action || undefined })

  return (
    <Card title="Audit log">
      <div className="stack">
        <div className="row align-end">
          <Field label="Show">
            <select value={action} onChange={(e) => setAction(e.target.value)}>
              {ACTIONS.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
          {workspaceId && (
            <span className="muted small">
              One workspace only.{' '}
              <Button variant="ghost" onClick={() => setParams({})}>
                Show every workspace
              </Button>
            </span>
          )}
        </div>
        {audit.isPending && <LoadingBlock />}
        {audit.isError && <ErrorNote error={audit.error} />}
        {audit.data?.length === 0 && <EmptyState title="Nothing recorded" body="Changes people make show up here as they happen." />}
        {audit.data && audit.data.length > 0 && (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr><th>When</th><th>Who</th><th>What</th><th>Details</th></tr>
              </thead>
              <tbody>
                {audit.data.map((entry) => (
                  <tr key={entry.id}>
                    <td className="muted" title={new Date(entry.created_at).toLocaleString()}>{timeAgo(entry.created_at)}</td>
                    <td>{entry.actor_email ?? <span className="muted">The system</span>}</td>
                    <td>
                      <code>{entry.action}</code>
                      {entry.target_type === 'workspace' && entry.target_id && (
                        <div className="small">
                          <Link to={`/studio/admin/workspaces/${entry.target_id}`}>Open the workspace</Link>
                        </div>
                      )}
                    </td>
                    <td className="muted small">{describe(entry)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Card>
  )
}
